import { useState } from "react";
import { SectionHeader } from "./SectionHeader";
import useReveal from "@/lib/useReveal";
import { useSection } from "@/lib/SiteContent";

const FALLBACK = { subtitle: "", days: [] };

const TimelineItem = ({ event, idx }) => {
  const ref = useReveal();
  return (
    <li
      ref={ref}
      className="reveal pl-8 md:pl-14 relative"
      data-testid={`program-item-${idx}`}
      style={{ transitionDelay: `${idx * 60}ms` }}
    >
      <span className="absolute -left-[6px] top-2 w-3 h-3 rounded-full bg-[#3D5E68]" />
      <p className="font-serif text-2xl md:text-3xl text-[#68919B]">{event.time}</p>
      <h3 className="font-serif text-xl md:text-2xl text-[#3D5E68] mt-1">{event.title}</h3>
      <p className="text-[#5A7682] font-light mt-2 max-w-md">{event.desc}</p>
    </li>
  );
};

export const Program = () => {
  const program = useSection("program", FALLBACK);
  const days = program.days || [];
  const defaultKey = days[1]?.key || days[0]?.key || "";
  const [activeKey, setActiveKey] = useState(defaultKey);
  const activeDay = days.find((d) => d.key === activeKey) || days[0];

  if (!activeDay) return null;

  return (
    <section
      id="program"
      data-testid="program-section"
      className="py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#EAEDDC]/40"
    >
      <div className="max-w-5xl mx-auto">
        <SectionHeader
          eyebrow="Kapittel II"
          title="Programmet"
          subtitle={program.subtitle}
          testId="program"
        />

        <div data-testid="program-day-selector" className="flex flex-nowrap justify-center gap-1 sm:gap-4 mb-16 md:mb-20">
          {days.map((d) => {
            const isActive = d.key === activeKey;
            return (
              <button
                key={d.key}
                type="button"
                data-testid={`program-day-tab-${d.key}`}
                onClick={() => setActiveKey(d.key)}
                className={`group flex flex-col items-center gap-1 px-3 sm:px-8 py-3 sm:py-4 transition-colors duration-300 border-b-2 ${
                  isActive ? "border-[#3D5E68]" : "border-transparent hover:border-[#68919B]"
                }`}
              >
                <span className={`text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] transition-colors ${isActive ? "text-[#68919B]" : "text-[#68919B]/70"}`}>
                  {d.date}
                </span>
                <span className={`font-serif text-lg sm:text-2xl md:text-3xl font-light transition-colors ${isActive ? "text-[#3D5E68]" : "text-[#5A7682] group-hover:text-[#3D5E68]"}`}>
                  {d.label}
                </span>
              </button>
            );
          })}
        </div>

        <p key={activeDay.key} className="text-center text-base md:text-lg text-[#5A7682] font-light italic mb-14 fade-up">
          {activeDay.subtitle}
        </p>

        <ol key={`tl-${activeDay.key}`} className="relative border-l border-[#68919B]/50 ml-3 md:ml-6 space-y-14">
          {(activeDay.events || []).map((e, idx) => (
            <TimelineItem key={`${activeDay.key}-${idx}`} event={e} idx={idx} />
          ))}
        </ol>
      </div>
    </section>
  );
};
